import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';

import type { ConservationSceneState } from './conservationExplore';
import {
  type LabSceneFactory,
  createLabStage,
  labGlassEdgeMaterial,
  labGlassMaterial,
  refractiveLiquidMaterial,
  seeded,
} from './labScene3d';

export type { ConservationSceneState } from './conservationExplore';

/**
 * Conservation of mass Explore view: a conical flask on a digital top-pan balance. The reading is
 * shown in the HTML beside the canvas. Solid left, precipitate, bubbles, the blue copper(II) colour
 * and the balloon all follow the same Explore result. A teaching view, not to scale.
 */

const BENCH_Y = -1.82;
const BALANCE = { width: 3.6, height: 0.5, depth: 2.9, panRadius: 1.45, panHeight: 0.06 } as const;
const PAN_TOP = BENCH_Y + BALANCE.height + 0.04 + BALANCE.panHeight;
const FLASK = { baseR: 1.12, neckR: 0.3, coneH: 2.15, neckH: 0.75, wall: 0.05 } as const;
const LIQUID_H = 0.85;
const MAX_CHIPS = 14;
const MAX_PRECIPITATE = 90;
const MAX_BUBBLES = 50;

/** Named states for screenshot review of every visible change. */
export const CONSERVATION_PREVIEW_STATES: Record<string, ConservationSceneState> = {
  'open-before': { closure: 'open', solid: 'chips', solidLeft: 0.8, precipitate: 0, blueSolution: 0, balloon: 0, bubbling: 0 },
  'open-fizzing': { closure: 'open', solid: 'chips', solidLeft: 0.4, precipitate: 0, blueSolution: 0, balloon: 0, bubbling: 0.86 },
  'balloon-half': { closure: 'balloon', solid: 'chips', solidLeft: 0.4, precipitate: 0, blueSolution: 0, balloon: 0.5, bubbling: 0.86 },
  'balloon-full': { closure: 'balloon', solid: 'tablet', solidLeft: 0, precipitate: 0, blueSolution: 0, balloon: 1, bubbling: 0 },
  'precipitate-forming': { closure: 'stopper', solid: 'none', solidLeft: 0, precipitate: 0.27, blueSolution: 0.5, balloon: 0, bubbling: 0 },
};

/** Inner radius of the cone at height y above the flask floor. */
function coneRadius(y: number): number {
  return FLASK.baseR - FLASK.wall - ((FLASK.baseR - FLASK.neckR) * y) / FLASK.coneH;
}

export const createConservationScene: LabSceneFactory<ConservationSceneState> = (renderer, palette, reducedMotion) => {
  const stage = createLabStage(renderer, palette, reducedMotion, { benchY: BENCH_Y, cameraPosition: [0.5, 3.9, 15.2], target: [0, 1.0, 0] });
  const { scene, keep } = stage;

  // Digital top-pan balance: opaque body, steel pan and a dark display window (the reading is in the HTML).
  const body = new THREE.Mesh(
    keep(new RoundedBoxGeometry(BALANCE.width, BALANCE.height, BALANCE.depth, 4, 0.1)),
    keep(new THREE.MeshPhysicalMaterial({ color: palette.metal.clone().lerp(palette.bench, 0.35), roughness: 0.45, clearcoat: 0.6 })),
  );
  body.position.y = BENCH_Y + BALANCE.height / 2;
  body.castShadow = true;
  body.receiveShadow = true;
  const steel = keep(new THREE.MeshPhysicalMaterial({ color: palette.bench.clone().lerp(palette.metal, 0.35), metalness: 0.9, roughness: 0.25 }));
  const stem = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.25, 0.3, 0.06, 32)), steel);
  stem.position.y = BENCH_Y + BALANCE.height + 0.02;
  const pan = new THREE.Mesh(keep(new THREE.CylinderGeometry(BALANCE.panRadius, BALANCE.panRadius, BALANCE.panHeight, 64)), steel);
  pan.position.y = PAN_TOP - BALANCE.panHeight / 2;
  pan.receiveShadow = true;
  pan.castShadow = true;
  const display = new THREE.Mesh(
    keep(new THREE.BoxGeometry(1.5, 0.26, 0.04)),
    keep(new THREE.MeshStandardMaterial({ color: palette.metal, emissive: palette.liquid.clone().lerp(palette.ice, 0.5), emissiveIntensity: 0.6 })),
  );
  display.position.set(0, BENCH_Y + BALANCE.height * 0.5, BALANCE.depth / 2 + 0.01);
  scene.add(body, stem, pan, display);

  // Conical flask: lathe profile with wall thickness, base ring and lip ring.
  const { baseR, neckR, coneH, neckH, wall } = FLASK;
  const top = coneH + neckH;
  const outline = [
    [0, 0], [baseR - 0.12, 0], [baseR - 0.02, 0.05], [baseR, 0.16], [neckR + 0.02, coneH], [neckR, coneH + 0.12], [neckR, top],
    [neckR + 0.06, top + 0.03], [neckR + 0.05, top + 0.08], [neckR - wall, top + 0.06], [neckR - wall, coneH + 0.12],
    [neckR - wall + 0.02, coneH - 0.04], [baseR - wall, 0.16 + wall], [baseR - 0.14, wall], [0, wall],
  ].map(([x, y]) => new THREE.Vector2(x, y));
  const flask = new THREE.Mesh(keep(new THREE.LatheGeometry(outline, 112)), labGlassMaterial(stage));
  flask.position.y = PAN_TOP;
  flask.renderOrder = 10;
  const edge = labGlassEdgeMaterial(stage);
  const baseRing = new THREE.Mesh(keep(new THREE.TorusGeometry(baseR - 0.05, 0.045, 12, 112)), edge);
  baseRing.rotation.x = Math.PI / 2;
  baseRing.position.y = PAN_TOP + 0.06;
  const lipRing = new THREE.Mesh(keep(new THREE.TorusGeometry(neckR + 0.03, 0.04, 12, 64)), edge);
  lipRing.rotation.x = Math.PI / 2;
  lipRing.position.y = PAN_TOP + top + 0.05;
  baseRing.renderOrder = 11;
  lipRing.renderOrder = 11;
  scene.add(flask, baseRing, lipRing);

  // Liquid: a refractive frustum whose colour shifts from clear to copper(II) blue.
  const floorY = PAN_TOP + wall;
  const clearColour = palette.liquid.clone().lerp(palette.glass, 0.85);
  const blueColour = palette.liquid.clone();
  const liquidMaterial = refractiveLiquidMaterial(stage, clearColour, { tint: 0.6, attenuationDistance: 8 });
  const liquid = new THREE.Mesh(keep(new THREE.CylinderGeometry(coneRadius(LIQUID_H) - 0.01, coneRadius(0) - 0.01, LIQUID_H, 96)), liquidMaterial);
  liquid.position.y = floorY + LIQUID_H / 2;
  scene.add(liquid);

  // Marble chips: opaque irregular lumps. Tablet: a white disc. Precipitate: opaque pale blue grains.
  const chipMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.bench.clone().lerp(palette.metal, 0.08), roughness: 0.85 }));
  const chips = new THREE.InstancedMesh(keep(new THREE.IcosahedronGeometry(0.16, 0)), chipMaterial, MAX_CHIPS);
  chips.castShadow = true;
  const tablet = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.42, 0.42, 0.12, 40)), chipMaterial);
  const precipitateMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.liquid.clone().lerp(palette.bench, 0.45), roughness: 0.9 }));
  const grains = new THREE.InstancedMesh(keep(new THREE.IcosahedronGeometry(0.05, 0)), precipitateMaterial, MAX_PRECIPITATE);
  scene.add(chips, tablet, grains);

  // Bubbles: transparent, never refractive.
  const bubbles = new THREE.InstancedMesh(
    keep(new THREE.SphereGeometry(0.045, 12, 8)),
    keep(new THREE.MeshPhysicalMaterial({ color: palette.steam, roughness: 0.05, transparent: true, opacity: 0.7, clearcoat: 1, envMapIntensity: 2.5 })),
    MAX_BUBBLES,
  );
  scene.add(bubbles);

  // Closures: rubber stopper, or a balloon over the neck that inflates with the trapped gas.
  const rubber = keep(new THREE.MeshPhysicalMaterial({ color: palette.metal.clone().lerp(palette.heat, 0.25), roughness: 0.7 }));
  const stopper = new THREE.Mesh(keep(new THREE.CylinderGeometry(neckR + 0.06, neckR - 0.04, 0.42, 32)), rubber);
  stopper.position.y = PAN_TOP + top + 0.05;
  const balloonMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.accent, roughness: 0.4, clearcoat: 0.8, sheen: 0.4 }));
  const balloon = new THREE.Mesh(keep(new THREE.SphereGeometry(1, 48, 32)), balloonMaterial);
  balloon.castShadow = true;
  const band = new THREE.Mesh(keep(new THREE.CylinderGeometry(neckR + 0.08, neckR + 0.08, 0.2, 32, 1, true)), balloonMaterial);
  band.position.y = PAN_TOP + top - 0.05;
  scene.add(stopper, balloon, band);

  stage.finish();

  const matrix = new THREE.Matrix4();
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const euler = new THREE.Euler();
  const scale = new THREE.Vector3();
  const hidden = new THREE.Matrix4().makeScale(0, 0, 0);
  const neckTop = PAN_TOP + top;

  return {
    update: (state, t, playing) => {
      const share = (value: number) => THREE.MathUtils.clamp(value, 0, 1);

      liquidMaterial.color.copy(clearColour).lerp(blueColour, share(state.blueSolution) * 0.8).lerp(palette.glass, 0.25);
      liquidMaterial.attenuationColor.copy(clearColour).lerp(blueColour, share(state.blueSolution));
      liquidMaterial.attenuationDistance = 8 - share(state.blueSolution) * 6.4;

      const chipCount = state.solid === 'chips' ? Math.round(share(state.solidLeft) * MAX_CHIPS) : 0;
      for (let index = 0; index < MAX_CHIPS; index += 1) {
        if (index >= chipCount) {
          chips.setMatrixAt(index, hidden);
          continue;
        }
        const angle = seeded(index, 1) * Math.PI * 2;
        const radius = Math.sqrt(seeded(index, 2)) * (coneRadius(0) - 0.3);
        position.set(Math.cos(angle) * radius, floorY + 0.1 + (index > 9 ? 0.14 : 0), Math.sin(angle) * radius);
        euler.set(seeded(index, 3) * 3, seeded(index, 4) * 3, seeded(index, 5) * 3);
        scale.set(0.8 + seeded(index, 6) * 0.6, 0.6 + seeded(index, 7) * 0.4, 0.8 + seeded(index, 8) * 0.5);
        chips.setMatrixAt(index, matrix.compose(position, quaternion.setFromEuler(euler), scale));
      }
      chips.instanceMatrix.needsUpdate = true;

      const tabletLeft = state.solid === 'tablet' ? share(state.solidLeft) : 0;
      tablet.visible = tabletLeft > 0.02;
      tablet.scale.set(Math.sqrt(tabletLeft), 1, Math.sqrt(tabletLeft));
      tablet.position.y = floorY + 0.06;

      const grainCount = Math.round(share(state.precipitate) * MAX_PRECIPITATE);
      for (let index = 0; index < MAX_PRECIPITATE; index += 1) {
        if (index >= grainCount) {
          grains.setMatrixAt(index, hidden);
          continue;
        }
        const angle = seeded(index, 11) * Math.PI * 2;
        const radius = Math.sqrt(seeded(index, 12)) * (coneRadius(0) - 0.12);
        // A few grains still settle through the liquid; most lie on the floor.
        const settling = seeded(index, 13) > 0.8 ? ((seeded(index, 14) + t * 0.05) % 1) * LIQUID_H * 0.8 : 0;
        position.set(Math.cos(angle) * radius, floorY + 0.04 + settling + Math.floor(index / 45) * 0.06, Math.sin(angle) * radius);
        grains.setMatrixAt(index, matrix.compose(position, quaternion.identity(), scale.setScalar(0.7 + seeded(index, 15) * 0.7)));
      }
      grains.instanceMatrix.needsUpdate = true;

      const bubbleCount = Math.round(share(state.bubbling) * MAX_BUBBLES);
      for (let index = 0; index < MAX_BUBBLES; index += 1) {
        if (index >= bubbleCount) {
          bubbles.setMatrixAt(index, hidden);
          continue;
        }
        const progress = (t * (0.4 + seeded(index, 21) * 0.5) + seeded(index, 22)) % 1;
        const y = progress * (LIQUID_H - 0.08);
        const angle = seeded(index, 23) * Math.PI * 2;
        const radius = Math.sqrt(seeded(index, 24)) * (coneRadius(y) - 0.12);
        position.set(Math.cos(angle) * radius + Math.sin(t * 4 + index) * 0.02, floorY + 0.12 + y, Math.sin(angle) * radius);
        bubbles.setMatrixAt(index, matrix.compose(position, quaternion.identity(), scale.setScalar(0.6 + progress)));
      }
      bubbles.instanceMatrix.needsUpdate = true;

      stopper.visible = state.closure === 'stopper';
      const hasBalloon = state.closure === 'balloon';
      balloon.visible = hasBalloon;
      band.visible = hasBalloon;
      if (hasBalloon) {
        // Volume follows the gas, so the radius grows with the cube root; an empty balloon hangs limp on the neck.
        const fill = share(state.balloon);
        const radius = 0.22 + 0.85 * Math.cbrt(fill);
        balloon.scale.set(radius, radius * (fill < 0.05 ? 0.6 : 1.12), radius);
        balloon.position.y = neckTop + radius * (fill < 0.05 ? 0.4 : 1.0);
      }

      stage.render(playing);
    },
    resize: stage.resize,
    dispose: stage.dispose,
  };
};
